import React, { useState, useEffect } from 'react';
import useQuranReader from '../hooks/useQuranReader';
import ThemeSelector from './ThemeSelector';
import OfflineSettings from './OfflineSettings';

const SettingsModal = ({ onClose }) => {
  const { 
    // Font settings
    showArabic, 
    setShowArabic,
    showTranslation, 
    setShowTranslation,
    showTransliteration, 
    setShowTransliteration,
    
    // Reciter and translation
    selectedReciter,
    setSelectedReciter,
    selectedTranslation,
    setSelectedTranslation,
    availableEditions,
    loadingEditions,
    
    // Progress reset
    resetProgress
  } = useQuranReader();
  
  // Font selection
  const [selectedFont, setSelectedFont] = useState(
    localStorage.getItem('quranReaderArabicFont') || 'scheherazade'
  );
  
  // Font size states
  const [arabicFontSize, setArabicFontSize] = useState(
    localStorage.getItem('quranReaderArabicFontSize') || '2xl'
  );
  const [translationFontSize, setTranslationFontSize] = useState(
    localStorage.getItem('quranReaderTranslationFontSize') || 'base'
  );
  const [transliterationFontSize, setTransliterationFontSize] = useState(
    localStorage.getItem('quranReaderTransliterationFontSize') || 'base'
  );
  
  // Font options
  const fonts = [
    { id: 'scheherazade', name: 'Scheherazade New', className: 'font-scheherazade' },
    { id: 'amiri', name: 'Amiri', className: 'font-amiri' },
    { id: 'uthmanic', name: 'Uthmanic Hafs', className: 'font-uthmanic' },
    { id: 'noto', name: 'Noto Naskh Arabic', className: 'font-noto' },
    { id: 'indopak', name: 'IndoPak', className: 'font-indopak' },
    { id: 'tajweed', name: 'Quran Tajweed', className: 'font-tajweed' },
    { id: 'dubai', name: 'Dubai', className: 'font-dubai' },
    { id: 'me-quran', name: 'Me Quran', className: 'font-me-quran' }
  ];
  
  // Font size options
  const fontSizes = [
    { value: 'xs', label: 'Extra Small' },
    { value: 'sm', label: 'Small' },
    { value: 'base', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra Large' },
    { value: '2xl', label: '2X Large' },
    { value: '3xl', label: '3X Large' },
  ];
  
  // Map Tailwind text sizes to actual CSS values
  const fontSizeValues = {
    'xs': '0.75rem',
    'sm': '0.875rem',
    'base': '1rem',
    'lg': '1.125rem',
    'xl': '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem'
  };
  
  // Save font settings
  const handleFontChange = (fontId) => {
    setSelectedFont(fontId);
    localStorage.setItem('quranReaderArabicFont', fontId);
    document.documentElement.setAttribute('data-arabic-font', fontId);
  };
  
  // Save font size settings
  const handleFontSizeChange = (type, size) => {
    if (type === 'arabic') {
      setArabicFontSize(size);
      localStorage.setItem('quranReaderArabicFontSize', size);
      document.documentElement.style.setProperty('--arabic-font-size', fontSizeValues[size]);
    } else if (type === 'translation') {
      setTranslationFontSize(size);
      localStorage.setItem('quranReaderTranslationFontSize', size);
      document.documentElement.style.setProperty('--translation-font-size', fontSizeValues[size]);
    } else if (type === 'transliteration') {
      setTransliterationFontSize(size);
      localStorage.setItem('quranReaderTransliterationFontSize', size);
      document.documentElement.style.setProperty('--transliteration-font-size', fontSizeValues[size]);
    }
  };
  
  // Apply stored settings on load
  useEffect(() => {
    document.documentElement.setAttribute('data-arabic-font', selectedFont);
    document.documentElement.style.setProperty('--arabic-font-size', fontSizeValues[arabicFontSize]);
    document.documentElement.style.setProperty('--translation-font-size', fontSizeValues[translationFontSize]);
    document.documentElement.style.setProperty('--transliteration-font-size', fontSizeValues[transliterationFontSize]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFont, arabicFontSize, translationFontSize, transliterationFontSize]);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Settings">
      <div 
        className="rounded-lg shadow-xl max-w-3xl w-full p-6 animate-fadeIn my-8 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}
      >
        <div 
          className="flex justify-between items-center mb-6 sticky top-0 pt-1 pb-3 border-b"
          style={{ 
            backgroundColor: 'var(--color-card-bg, #FFFFFF)', 
            borderColor: 'var(--color-card-border, #F3F4F6)' 
          }}
        >
          <h2 className="text-2xl font-bold text-heading font-kufi">Settings</h2>
          <button 
            onClick={onClose}
            className="opacity-60 hover:opacity-100 focus:outline-none"
            style={{ color: 'var(--color-charcoal)' }}
            aria-label="Close settings"
          >
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Theme, Text Display & Audio */}
          <div>
            {/* Theme Selector - NEW */}
            <ThemeSelector />

            {/* Display options */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Text Display
              </h3>
              
              <div className="space-y-3 mb-4">
                <label className="flex items-center space-x-3 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  <input 
                    type="checkbox" 
                    checked={showArabic} 
                    onChange={() => setShowArabic(!showArabic)}
                    className="h-4 w-4 rounded accent-deep-green focus:ring-2 focus:ring-deep-green"
                  />
                  <span>Show Arabic Text</span>
                </label>
                
                <label className="flex items-center space-x-3 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  <input 
                    type="checkbox" 
                    checked={showTranslation} 
                    onChange={() => setShowTranslation(!showTranslation)}
                    className="h-4 w-4 rounded accent-deep-green focus:ring-2 focus:ring-deep-green"
                  />
                  <span>Show Translation</span>
                </label>
                
                <label className="flex items-center space-x-3 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  <input 
                    type="checkbox" 
                    checked={showTransliteration} 
                    onChange={() => setShowTransliteration(!showTransliteration)}
                    className="h-4 w-4 rounded accent-deep-green focus:ring-2 focus:ring-deep-green"
                  />
                  <span>Show Transliteration</span>
                </label>
              </div>
            </div>
          
            {/* Audio settings */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Recitation
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Reciter
                </label>
                <select 
                  className="w-full p-2 border rounded-md shadow-sm font-lato"
                  style={{ 
                    borderColor: 'var(--color-input-border)', 
                    backgroundColor: 'var(--color-input-bg)',
                    color: 'var(--color-charcoal)' 
                  }}
                  value={selectedReciter}
                  onChange={(e) => setSelectedReciter(e.target.value)}
                  disabled={loadingEditions}
                >
                  {loadingEditions ? (
                    <option>Loading reciters...</option>
                    ) : (
                    availableEditions.reciters.map(reciter => {
                        const langCode = reciter.identifier.split('.')[0].toUpperCase();
                        return (
                        <option key={reciter.identifier} value={reciter.identifier}>
                            {reciter.englishName} - {langCode}
                        </option>
                        );
                    })
                    )}
                </select>
              </div>
            </div>
            
            {/* Translation selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Translation
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Translation Edition
                </label>
                <select 
                  className="w-full p-2 border rounded-md shadow-sm font-lato"
                  style={{ 
                    borderColor: 'var(--color-input-border)', 
                    backgroundColor: 'var(--color-input-bg)',
                    color: 'var(--color-charcoal)' 
                  }}
                  value={selectedTranslation}
                  onChange={(e) => setSelectedTranslation(e.target.value)}
                  disabled={loadingEditions}
                >
                  {loadingEditions ? (
                    <option>Loading translations...</option>
                  ) : (
                    availableEditions.translations.map(translation => {
                      const languageDisplay = translation.language === 'en' ? 'English' : 
                        new Intl.DisplayNames(['en'], { type: 'language' }).of(translation.language);
                      return (
                        <option key={translation.identifier} value={translation.identifier}>
                          {languageDisplay} - {translation.englishName}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>
            </div>

            {/* Font Size Settings */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Font Size
              </h3>
              
              {/* Arabic Font Size */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Arabic Text Size
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={arabicFontSize}
                    onChange={(e) => handleFontSizeChange('arabic', e.target.value)}
                    className="p-2 border rounded-md shadow-sm font-lato"
                    style={{ 
                      borderColor: 'var(--color-input-border)', 
                      backgroundColor: 'var(--color-input-bg)',
                      color: 'var(--color-charcoal)' 
                    }}
                  >
                    {fontSizes.map(size => (
                      <option key={`arabic-${size.value}`} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                  <div 
                    className="ml-2 p-2 rounded flex-grow text-center"
                    style={{ backgroundColor: 'var(--color-light-gray)' }}
                    dir="rtl"
                  >
                    <span className="arabic-text" style={{fontSize: fontSizeValues[arabicFontSize], color: 'var(--color-charcoal)'}}>بِسْمِ اللَّهِ</span>
                  </div>
                </div>
              </div>
              
              {/* Translation Font Size */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Translation Text Size
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={translationFontSize}
                    onChange={(e) => handleFontSizeChange('translation', e.target.value)}
                    className="p-2 border rounded-md shadow-sm font-lato"
                    style={{ 
                      borderColor: 'var(--color-input-border)', 
                      backgroundColor: 'var(--color-input-bg)',
                      color: 'var(--color-charcoal)' 
                    }}
                  >
                    {fontSizes.map(size => (
                      <option key={`translation-${size.value}`} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                  <div 
                    className="ml-2 p-2 rounded flex-grow text-center"
                    style={{ backgroundColor: 'var(--color-light-gray)' }}
                  >
                    <span className="font-lato" style={{fontSize: fontSizeValues[translationFontSize], color: 'var(--color-charcoal)'}}>In the name of Allah</span>
                  </div>
                </div>
              </div>
              
              {/* Transliteration Font Size */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Transliteration Text Size
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={transliterationFontSize}
                    onChange={(e) => handleFontSizeChange('transliteration', e.target.value)}
                    className="p-2 border rounded-md shadow-sm font-lato"
                    style={{ 
                      borderColor: 'var(--color-input-border)', 
                      backgroundColor: 'var(--color-input-bg)',
                      color: 'var(--color-charcoal)' 
                    }}
                  >
                    {fontSizes.map(size => (
                      <option key={`transliteration-${size.value}`} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                  <div 
                    className="ml-2 p-2 rounded flex-grow text-center"
                    style={{ backgroundColor: 'var(--color-light-gray)' }}
                  >
                    <span className="font-lato italic" style={{fontSize: fontSizeValues[transliterationFontSize], color: 'var(--color-charcoal)'}}>Bismillāhi r-raḥmāni r-raḥīm</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Offline */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Offline reading
              </h3>
              <OfflineSettings />
            </div>

            {/* Reset Progress */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Reading Progress
              </h3>
              
              <button 
                onClick={resetProgress}
                className="px-4 py-2 rounded-md transition-colors font-lato"
                style={{ backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
              >
                Reset Reading Progress
              </button>
              <p className="text-xs mt-2" style={{ color: 'var(--color-charcoal)', opacity: 0.5 }}>
                This will clear your saved reading position and start from the beginning.
              </p>
            </div>
          </div>
          
          {/* Right Column - Font Settings */}
          <div>
            {/* Arabic Font */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-heading border-b pb-2" style={{ borderColor: 'var(--color-card-border, #F3F4F6)' }}>
                Arabic Font
              </h3>
              
              <div className="space-y-4 mb-4">
                {fonts.map(font => (
                  <div 
                    key={font.id} 
                    className={`p-3 rounded-lg border transition-colors cursor-pointer`}
                    style={{
                      borderColor: selectedFont === font.id ? 'var(--color-heading)' : 'var(--color-card-border)',
                      backgroundColor: selectedFont === font.id ? 'var(--color-soft-ivory)' : 'var(--color-card-bg)',
                    }}
                    onClick={() => handleFontChange(font.id)}
                  >
                    <div className="flex items-center mb-2">
                      <input
                        type="radio"
                        id={`font-${font.id}`}
                        name="arabic-font"
                        value={font.id}
                        checked={selectedFont === font.id}
                        onChange={() => handleFontChange(font.id)}
                        className="h-4 w-4 accent-deep-green focus:ring-2 focus:ring-deep-green"
                      />
                      <label
                        htmlFor={`font-${font.id}`}
                        className="ml-3 block font-lato font-medium"
                        style={{ color: 'var(--color-charcoal)' }}
                      >
                        {font.name}
                      </label>
                    </div>
                    <div 
                      className="p-3 rounded-md text-center" 
                      style={{ backgroundColor: 'var(--color-light-gray)' }}
                      dir="rtl"
                    >
                      <span className={`block ${font.className}`} style={{fontSize: fontSizeValues[arabicFontSize], color: 'var(--color-charcoal)'}}>بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</span>
                      <span className="text-xs mt-1 block font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.5 }} dir="ltr">Bismillāhi r-raḥmāni r-raḥīm</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-card-border, #E5E7EB)' }}>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-deep-green text-white rounded-md hover:bg-medium-green transition-colors font-lato"
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;